'use client'

import { useEffect } from 'react'
import { createLogger, LogLevel } from '@/lib/logger'

// Force Info level so the activation message appears even in production builds
// (default client level in prod is Warn). Perf tooling is only used against
// `pnpm build && pnpm start`, so this message has to come through there.
const logger = createLogger('perf', { level: LogLevel.Info })

// Companion to src/lib/perf.ts. Mounted once in (app)/layout.tsx. Detects the
// activation paths (URL param, localStorage, env var), wires up a keyboard
// shortcut for printing the report, and silently no-ops when disabled.
//
// Usage guide: docs/testing/performance-profiling.md
export default function DevPerfReport() {
  useEffect(() => {
    if (typeof window === 'undefined') return

    const urlEnabled = new URLSearchParams(window.location.search).get('perf') === '1'
    let storageEnabled = false
    try {
      storageEnabled = window.localStorage.getItem('__PERF__') === '1'
    } catch {
      // localStorage can throw in private mode or when storage is disabled
    }
    const envEnabled = process.env.NEXT_PUBLIC_PERF === '1'
    const enabled = urlEnabled || storageEnabled || envEnabled

    if (!enabled) return

    if (window.__PERF__ === undefined) {
      window.__PERF__ = true
    }

    // If activated via URL param, persist so refreshes keep working
    if (urlEnabled && !storageEnabled) {
      try {
        window.localStorage.setItem('__PERF__', '1')
      } catch {
        // ignore
      }
    }

    const handler = (event: KeyboardEvent) => {
      const modKey = event.metaKey || event.ctrlKey
      if (!modKey || !event.shiftKey) return
      const key = event.key.toLowerCase()
      if (key === 'p') {
        event.preventDefault()
        window.__perf?.report()
      } else if (key === 'c') {
        event.preventDefault()
        window.__perf?.clear()
      }
    }
    window.addEventListener('keydown', handler)

    logger.info(
      'enabled. Interact with the page, then run `window.__perf.report()` in the console. ' +
        'Disable: `localStorage.removeItem("__PERF__")` then reload.',
    )
    return () => window.removeEventListener('keydown', handler)
  }, [])

  return null
}
